import { apiClient } from '@/api/client';
import { z } from 'zod';
import { mapFeedItemWireToDomain, mapLikeResponseWireToDomain, mapPaginatedFeedWireToDomain, mapPaginatedLikersWireToDomain } from '../mappers/feed';
import {
  feedItemWireSchema,
  likeResponseWireSchema,
  paginatedFeedResponseWireSchema,
  paginatedLikersResponseWireSchema,
  type FeedItemWire,
  type LikeResponseWire,
  type PaginatedFeedResponseWire,
  type PaginatedLikersResponseWire,
} from '../schemas/feed';
import type { FeedItem, LikeResponse, PaginatedFeedResponse, PaginatedLikersResponse } from '@/types/feed';

/**
 * Domain-oriented API module for activity feed
 */

export interface FeedParams {
  page?: number;
  per_page?: number;
}

export interface NearbyFeedParams extends FeedParams {
  latitude: number;
  longitude: number;
  radius_km?: number;
}

/**
 * Build query string from feed params
 */
function buildFeedQueryString(params: FeedParams, extra?: Record<string, string>): string {
  const queryParams: string[] = [];
  if (params.page !== undefined) queryParams.push(`page=${params.page}`);
  if (params.per_page !== undefined) queryParams.push(`per_page=${params.per_page}`);
  if (extra) {
    for (const [key, value] of Object.entries(extra)) {
      queryParams.push(`${key}=${encodeURIComponent(value)}`);
    }
  }
  return queryParams.length > 0 ? `?${queryParams.join('&')}` : '';
}

/**
 * Validate and map feed response
 */
function parseFeedResponse(wireData: unknown): PaginatedFeedResponse {
  let validatedWire: PaginatedFeedResponseWire;
  try {
    validatedWire = paginatedFeedResponseWireSchema.parse(wireData);
  } catch (error) {
    if (error instanceof z.ZodError) {
      console.error('[API] Feed data validation failed:', error.issues);
      const parseResult = paginatedFeedResponseWireSchema.safeParse(wireData);
      if (parseResult.success) {
        validatedWire = parseResult.data;
      } else {
        throw new Error('Invalid feed data received from server');
      }
    } else {
      throw error;
    }
  }
  return mapPaginatedFeedWireToDomain(validatedWire);
}

/**
 * Get activity feed (friends + self check-ins)
 * Calls GET /api/feed?include_self=true
 */
export async function getFeed(params: FeedParams = {}): Promise<PaginatedFeedResponse> {
  const queryString = buildFeedQueryString(params, { include_self: 'true' });
  const wireData = await apiClient.get<PaginatedFeedResponseWire>(
    `/api/feed${queryString}`
  );
  return parseFeedResponse(wireData);
}

export async function getNearbyFeed(params: NearbyFeedParams): Promise<PaginatedFeedResponse> {
  const extra: Record<string, string> = {
    latitude: String(params.latitude),
    longitude: String(params.longitude),
    include_self: 'true',
  };
  if (params.radius_km !== undefined) {
    extra.radius_km = String(params.radius_km);
  }
  const queryString = buildFeedQueryString(params, extra);
  const wireData = await apiClient.get<PaginatedFeedResponseWire>(
    `/api/feed/nearby${queryString}`
  );
  return parseFeedResponse(wireData);
}

/**
 * Like a check-in
 * Calls POST /api/feed/{checkInId}/like
 */
export async function likeCheckIn(checkInId: string): Promise<LikeResponse> {
  const wireData = await apiClient.post<LikeResponseWire>(`/api/feed/${checkInId}/like`);
  const validated = likeResponseWireSchema.parse(wireData);
  return mapLikeResponseWireToDomain(validated);
}

/**
 * Unlike a check-in
 * Calls DELETE /api/feed/{checkInId}/like
 */
export async function unlikeCheckIn(checkInId: string): Promise<LikeResponse> {
  const wireData = await apiClient.delete<LikeResponseWire>(`/api/feed/${checkInId}/like`);
  const validated = likeResponseWireSchema.parse(wireData);
  return mapLikeResponseWireToDomain(validated);
}

/**
 * Get likers for a check-in
 * Calls GET /api/feed/{checkInId}/likes
 */
export async function getCheckInLikers(
  checkInId: string,
  params: FeedParams = {}
): Promise<PaginatedLikersResponse> {
  const queryString = buildFeedQueryString(params);
  const wireData = await apiClient.get<PaginatedLikersResponseWire>(
    `/api/feed/${checkInId}/likes${queryString}`
  );
  const validated = paginatedLikersResponseWireSchema.parse(wireData);
  return mapPaginatedLikersWireToDomain(validated);
}

/**
 * Get a specific user's feed (check-in history)
 * Calls GET /api/users/{user_id}/feed
 */
export async function getUserFeed(
  userId: string,
  params: FeedParams = {}
): Promise<PaginatedFeedResponse> {
  const queryString = buildFeedQueryString(params);
  const wireData = await apiClient.get<PaginatedFeedResponseWire>(
    `/api/users/${userId}/feed${queryString}`
  );
  return parseFeedResponse(wireData);
}

/**
 * Params for business feed with optional filters
 */
export interface BusinessFeedParams extends FeedParams {
  rating_key?: string;
  menu_item_ids?: string[];
  search?: string;
}

/**
 * Get check-in feed for a specific business
 * Calls GET /api/feed/business/{businessId}
 */
export async function getBusinessFeed(
  businessId: string,
  params: BusinessFeedParams = {}
): Promise<PaginatedFeedResponse> {
  const extra: Record<string, string> = {};
  if (params.rating_key) extra.rating_key = params.rating_key;
  if (params.menu_item_ids && params.menu_item_ids.length > 0) extra.menu_item_ids = params.menu_item_ids.join(',');
  if (params.search) extra.search = params.search;
  const queryString = buildFeedQueryString(params, extra);
  const wireData = await apiClient.get<PaginatedFeedResponseWire>(
    `/api/feed/business/${businessId}${queryString}`
  );
  return parseFeedResponse(wireData);
}

/**
 * Get check-in feed for a specific menu item
 * Calls GET /api/feed/menu-item/{menuItemId}
 */
export async function getMenuItemFeed(
  menuItemId: string,
  params: FeedParams = {}
): Promise<PaginatedFeedResponse> {
  const queryString = buildFeedQueryString(params);
  const wireData = await apiClient.get<PaginatedFeedResponseWire>(
    `/api/feed/menu-item/${menuItemId}${queryString}`
  );
  return parseFeedResponse(wireData);
}

/**
 * Get a single check-in as a feed item
 * Calls GET /api/feed/check-in/{checkInId}
 */
export async function getCheckInFeedItem(checkInId: string): Promise<FeedItem> {
  const wireData = await apiClient.get<FeedItemWire>(
    `/api/feed/check-in/${checkInId}`
  );
  const validatedWire = feedItemWireSchema.parse(wireData);
  return mapFeedItemWireToDomain(validatedWire);
}
